import {ExternalLinkIcon, InfoIcon, Loader2Icon, XIcon} from "lucide-react";
import {type ReactNode, useCallback, useEffect, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {
    fetchDrilldown,
    fetchLedgerRows,
    fetchProvenance,
    type LedgerFilters,
    LedgerRow,
    SourceBreakdown,
    ThreeStatementAssumptionRef,
    ThreeStatementCellProvenance,
    ThreeStatementCheck,
    ThreeStatementDependencyRef,
    ThreeStatementScheduleRef,
    ThreeStatementSourceRef,
    TopContact,
    VNextDrilldownData,
    VNextProvenanceData,
} from "@hooks/use-vnext";

function lastDayOfMonth(monthKey: string): string {
    const [yyyy, mm] = monthKey.split("-").map(Number);
    const d = new Date(yyyy, mm, 0);
    return `${yyyy}-${String(mm).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface ProvenancePanelProps {
    workspaceId: string;
    scenarioId: string;
    nodeId: string;
    nodeLabel: string;
    monthKey: string;
    monthLabel: string;
    value: number;
    isForecast: boolean;
    onClose: () => void;
    onOpenTransactions?: (filters: LedgerFilters) => void;
    source?: "pnl" | "cashflow";
    accountCode?: string;
    initialProvenance?: ThreeStatementCellProvenance | null;
}

function formatCurrency(v: number | null | undefined): string {
    if (v == null || Number.isNaN(v)) return "—";
    return v.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0});
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
    ACCREC: "Invoice",
    ACCPAY: "Bill",
    ACCRECCREDIT: "Credit Note",
    ACCPAYCREDIT: "Supplier Credit",
    MANJOURNAL: "Manual Journal",
    CASHPAID: "Cash Payment",
    CASHRECEIVED: "Cash Receipt",
    TRANSFER: "Transfer",
};

function formatSourceType(src: string): string {
    return SOURCE_TYPE_LABELS[src] || src;
}

function formatTimestamp(value?: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function formatOpSummary(op: Record<string, unknown>): string {
    const params = op.params as Record<string, unknown> | undefined;
    if (!params) return String(op.label || op.op_type || "Scenario operation");

    const driverType = params.driver_type ? String(params.driver_type) : null;
    const value = typeof params.value === "number" ? params.value : null;
    const effectiveFrom = params.effective_from ? String(params.effective_from) : null;
    const effectiveTo = params.effective_to ? String(params.effective_to) : null;

    const parts = [
        driverType,
        value != null ? String(value) : null,
        effectiveFrom ? `from ${effectiveFrom}` : null,
        effectiveTo ? `to ${effectiveTo}` : null,
    ].filter(Boolean);

    return parts.length > 0
        ? `${String(op.label || op.op_type || "Scenario operation")} — ${parts.join(" • ")}`
        : String(op.label || op.op_type || "Scenario operation");
}

function DetailListSection({
    title,
    children,
}: {
    title: string;
    children: ReactNode;
}) {
    return (
        <div className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {title}
            </h4>
            <div className="space-y-1">{children}</div>
        </div>
    );
}

function formatCheckDelta(check: ThreeStatementCheck): string | null {
    if (check.delta == null) return null;
    return `${check.delta > 0 ? "+" : ""}${check.delta.toLocaleString(undefined, {maximumFractionDigits: 2})}`;
}

const LEDGER_PAGE_SIZE = 20;

export function ProvenancePanel({
    workspaceId,
    scenarioId,
    nodeId,
    nodeLabel,
    monthKey,
    monthLabel,
    value,
    isForecast,
    onClose,
    onOpenTransactions,
    source = "pnl",
    accountCode = "",
    initialProvenance = null,
}: ProvenancePanelProps) {
    const [provenance, setProvenance] = useState<VNextProvenanceData | null>(null);
    const [drilldown, setDrilldown] = useState<VNextDrilldownData | null>(null);
    const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
    const [ledgerTotal, setLedgerTotal] = useState<number>(0);
    const [ledgerCount, setLedgerCount] = useState<number>(0);
    const [sourceBreakdown, setSourceBreakdown] = useState<SourceBreakdown[]>([]);
    const [topContacts, setTopContacts] = useState<TopContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeSourceFilter, setActiveSourceFilter] = useState<string | null>(null);
    const [ledgerOffset, setLedgerOffset] = useState(0);

    const loadLedger = useCallback(async (offset: number, sourceType?: string, append = false) => {
        if (!accountCode && !nodeId) return null;
        try {
            const ledger = await fetchLedgerRows(
                workspaceId, monthKey, accountCode, source, LEDGER_PAGE_SIZE, offset, nodeId, sourceType || undefined,
            );
            if (append) {
                setLedgerRows(prev => [...prev, ...ledger.rows]);
            } else {
                setLedgerRows(ledger.rows);
            }
            setLedgerTotal(ledger.total_net);
            setLedgerCount(ledger.count);
            setSourceBreakdown(ledger.source_breakdown ?? []);
            setTopContacts(ledger.top_contacts ?? []);
            return ledger;
        } catch (e) {
            console.error("Drill-down ledger fetch failed:", e);
            return null;
        }
    }, [workspaceId, monthKey, accountCode, source, nodeId]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setLedgerOffset(0);
        setActiveSourceFilter(null);
        try {
            const [prov, drill] = await Promise.all([
                (initialProvenance
                    ? Promise.resolve({
                        node_id: nodeId,
                        label: nodeLabel,
                        month_key: monthKey,
                        provenance: initialProvenance,
                        lineage: [],
                    })
                    : fetchProvenance(workspaceId, nodeId, monthKey, scenarioId)
                ).catch((e) => { console.error("Provenance fetch failed:", e); return null; }),
                fetchDrilldown(workspaceId, nodeId, monthKey).catch((e) => { console.error("Drilldown fetch failed:", e); return null; }),
            ]);
            setProvenance(prov);
            setDrilldown(drill);

            const ledger = await loadLedger(0);
            if (!ledger) {
                setLedgerRows([]);
                setLedgerTotal(0);
                setLedgerCount(0);
                setSourceBreakdown([]);
                setTopContacts([]);
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load provenance");
        } finally {
            setLoading(false);
        }
    }, [workspaceId, scenarioId, nodeId, nodeLabel, monthKey, loadLedger, initialProvenance]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSourceFilter = useCallback(async (sourceType: string) => {
        const newFilter = activeSourceFilter === sourceType ? null : sourceType;
        setActiveSourceFilter(newFilter);
        setLedgerOffset(0);
        setLoading(true);
        await loadLedger(0, newFilter || undefined);
        setLoading(false);
    }, [activeSourceFilter, loadLedger]);

    const handleLoadMore = useCallback(async () => {
        const newOffset = ledgerOffset + LEDGER_PAGE_SIZE;
        setLoadingMore(true);
        setLedgerOffset(newOffset);
        await loadLedger(newOffset, activeSourceFilter || undefined, true);
        setLoadingMore(false);
    }, [ledgerOffset, activeSourceFilter, loadLedger]);

    const prov = provenance?.provenance;
    const lineage = drilldown?.lineage ?? [];
    const breakdown = drilldown?.breakdown ?? [];
    const allLoaded = ledgerRows.length >= ledgerCount;
    const deltaFromBaseline = prov ? prov.value - prov.baseline_value : 0;

    // Filtered total: sum of visible rows when filter active
    const visibleTotal = activeSourceFilter
        ? ledgerRows.reduce((sum, r) => sum + r.net_amount, 0)
        : ledgerTotal;

    return (
        <div className="fixed right-0 top-0 z-50 flex h-full w-[420px] flex-col border-l bg-background shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between border-b p-4">
                <div className="flex items-center gap-2">
                    <InfoIcon className="size-4 text-[#209DB3]" />
                    <h3 className="font-semibold text-sm">Cell Detail</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={onClose}>
                    <XIcon className="size-4" />
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Cell info */}
                <div className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">{nodeLabel}</p>
                    <p className="text-xs text-muted-foreground">{monthLabel}</p>
                    <p className="text-2xl font-bold tabular-nums">
                        ${formatCurrency(value)}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={isForecast ? "outline" : "default"} className="text-xs">
                            {isForecast ? "Forecast" : "Actual"}
                        </Badge>
                        {prov?.cell_type && (
                            <Badge variant="secondary" className="text-[10px]">
                                {prov.cell_type}
                            </Badge>
                        )}
                        {prov?.is_override && (
                            <Badge className="bg-amber-100 text-[10px] text-amber-700">
                                Override
                            </Badge>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {error && <p className="text-sm text-red-600">{error}</p>}

                {/* Computation from provenance */}
                {prov && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Computation
                        </h4>
                        <div className="rounded-md border p-3 space-y-2 text-sm">
                            {prov.calculation && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Method</span>
                                    <span className="font-mono text-xs">{prov.calculation}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Baseline</span>
                                <span className="tabular-nums font-medium">
                                    ${formatCurrency(prov.baseline_value)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Current</span>
                                <span className="tabular-nums font-medium">
                                    ${formatCurrency(prov.value)}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Delta</span>
                                <span className={`tabular-nums font-medium ${deltaFromBaseline >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                    {deltaFromBaseline >= 0 ? "+" : ""}${formatCurrency(deltaFromBaseline)}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {prov && (
                    <DetailListSection title="Metadata">
                        <div className="rounded border p-2 text-xs">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <p className="text-muted-foreground">Node</p>
                                    <p className="font-medium">{prov.node_id}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Computed</p>
                                    <p className="font-medium">{formatTimestamp(prov.computed_at)}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Source Type</p>
                                    <p className="font-medium">{prov.source_type ? formatSourceType(prov.source_type) : "—"}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Source Label</p>
                                    <p className="font-medium">{prov.source_label || "—"}</p>
                                </div>
                            </div>
                        </div>
                    </DetailListSection>
                )}

                {prov?.ops_applied && prov.ops_applied.length > 0 && (
                    <DetailListSection title="Scenario Operations">
                        <div className="space-y-1">
                            {prov.ops_applied.map((op, i) => (
                                <div key={i} className="rounded border p-2 text-xs">
                                    <p className="font-medium">{formatOpSummary(op)}</p>
                                </div>
                            ))}
                        </div>
                    </DetailListSection>
                )}

                {prov?.source_refs && prov.source_refs.length > 0 && (
                    <DetailListSection title="Sources">
                        {prov.source_refs.map((sourceRef: ThreeStatementSourceRef, idx: number) => (
                            <div key={`${sourceRef.source_type}-${sourceRef.source_id ?? idx}`} className="rounded border p-2 text-xs">
                                <div className="flex justify-between gap-3">
                                    <span className="font-medium">
                                        {sourceRef.source_label || formatSourceType(sourceRef.source_type)}
                                    </span>
                                    {sourceRef.amount != null && (
                                        <span className="tabular-nums font-medium">${formatCurrency(sourceRef.amount)}</span>
                                    )}
                                </div>
                                <div className="text-muted-foreground">
                                    {[sourceRef.account_code, sourceRef.account_name].filter(Boolean).join(" – ") || formatSourceType(sourceRef.source_type)}
                                    {sourceRef.sync_timestamp ? ` • synced ${formatTimestamp(sourceRef.sync_timestamp)}` : ""}
                                </div>
                            </div>
                        ))}
                    </DetailListSection>
                )}

                {prov?.assumptions_used && prov.assumptions_used.length > 0 && (
                    <DetailListSection title="Assumptions">
                        {prov.assumptions_used.map((assumption: ThreeStatementAssumptionRef) => (
                            <div key={assumption.key} className="rounded border p-2 text-xs">
                                <div className="flex justify-between gap-3">
                                    <span className="font-medium">{assumption.label}</span>
                                    <span className="tabular-nums font-medium">
                                        {String(assumption.value ?? "—")}{assumption.unit ? ` ${assumption.unit}` : ""}
                                    </span>
                                </div>
                                {assumption.description && (
                                    <p className="text-muted-foreground mt-1">{assumption.description}</p>
                                )}
                            </div>
                        ))}
                    </DetailListSection>
                )}

                {prov?.schedule_refs && prov.schedule_refs.length > 0 && (
                    <DetailListSection title="Schedules">
                        {prov.schedule_refs.map((scheduleRef: ThreeStatementScheduleRef) => (
                            <div key={`${scheduleRef.key}-${scheduleRef.month_key ?? "all"}`} className="rounded border p-2 text-xs">
                                <div className="flex justify-between gap-3">
                                    <span className="font-medium">{scheduleRef.label}</span>
                                    {scheduleRef.value != null && (
                                        <span className="tabular-nums font-medium">${formatCurrency(scheduleRef.value)}</span>
                                    )}
                                </div>
                                <p className="text-muted-foreground">
                                    {scheduleRef.month_key ?? "all forecast months"}
                                    {scheduleRef.description ? ` • ${scheduleRef.description}` : ""}
                                </p>
                            </div>
                        ))}
                    </DetailListSection>
                )}

                {prov?.linked_dependencies && prov.linked_dependencies.length > 0 && (
                    <DetailListSection title="Linked Dependencies">
                        {prov.linked_dependencies.map((dependency: ThreeStatementDependencyRef, idx: number) => (
                            <div key={`${dependency.statement}-${dependency.node_id ?? idx}`} className="rounded border p-2 text-xs">
                                <div className="flex justify-between gap-3">
                                    <span className="font-medium">{dependency.node_label}</span>
                                    {dependency.value != null && (
                                        <span className="tabular-nums font-medium">${formatCurrency(dependency.value)}</span>
                                    )}
                                </div>
                                <p className="text-muted-foreground">
                                    {dependency.statement.toUpperCase()}
                                    {dependency.month_key ? ` • ${dependency.month_key}` : ""}
                                    {dependency.relation ? ` • ${dependency.relation}` : ""}
                                </p>
                            </div>
                        ))}
                    </DetailListSection>
                )}

                {prov?.checks && prov.checks.length > 0 && (
                    <DetailListSection title="Reconciliation Context">
                        {prov.checks.map((check: ThreeStatementCheck) => (
                            <div key={`${check.key}-${check.month_key ?? "all"}`} className="rounded border p-2 text-xs">
                                <div className="flex justify-between gap-3">
                                    <span className="font-medium">{check.label}</span>
                                    <Badge variant="secondary" className="text-[10px]">
                                        {check.status}
                                    </Badge>
                                </div>
                                <p className="text-muted-foreground">
                                    {check.statement ? check.statement.toUpperCase() : "SYSTEM"}
                                    {check.month_key ? ` • ${check.month_key}` : ""}
                                    {check.reason ? ` • ${check.reason}` : ""}
                                </p>
                                {formatCheckDelta(check) && (
                                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">Δ {formatCheckDelta(check)}</p>
                                )}
                            </div>
                        ))}
                    </DetailListSection>
                )}

                {/* Drilldown - leaf node source transactions */}
                {lineage.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Source Transactions ({lineage.length})
                        </h4>
                        <div className="space-y-1">
                            {lineage.map((item) => (
                                <div key={item.id} className="rounded border p-2 text-xs">
                                    <div className="flex justify-between">
                                        <span className="font-medium">
                                            {item.description || "Transaction"}
                                        </span>
                                        <span className="tabular-nums font-medium">
                                            ${formatCurrency(item.amount)}
                                        </span>
                                    </div>
                                    <span className="text-muted-foreground">
                                        {item.source_type}
                                        {item.source_id ? ` / ${item.source_id}` : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Drilldown - aggregation node breakdown */}
                {breakdown.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Breakdown ({breakdown.length} items)
                            {drilldown?.total != null && (
                                <span className="ml-1 font-normal">
                                    — Total: ${formatCurrency(drilldown.total)}
                                </span>
                            )}
                        </h4>
                        <div className="space-y-1">
                            {breakdown.map((item) => (
                                <div key={item.node_id} className="rounded border p-2 text-xs">
                                    <div className="flex justify-between">
                                        <span className="font-medium">{item.label}</span>
                                        <span className="tabular-nums font-medium">
                                            ${formatCurrency(item.value)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>{item.category_key}</span>
                                        <span>{(item.weight * 100).toFixed(1)}%</span>
                                    </div>
                                </div>
                            ))}
                            {drilldown?.has_more && (
                                <p className="text-xs text-muted-foreground text-center py-1">
                                    More items not shown...
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Ledger Transactions */}
                {ledgerRows.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Ledger Transactions ({ledgerRows.length} of {ledgerCount})
                        </h4>
                        {ledgerRows[0]?.account_name && (
                            <p className="text-xs text-muted-foreground">
                                {ledgerRows[0].account_code ? `${ledgerRows[0].account_code} – ` : ""}
                                {ledgerRows[0].account_name}
                            </p>
                        )}
                        {/* Source breakdown badges — clickable filters */}
                        {sourceBreakdown.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {sourceBreakdown.map((sb) => (
                                    <Badge
                                        key={sb.source_type}
                                        variant={activeSourceFilter === sb.source_type ? "default" : "secondary"}
                                        className="text-[10px] cursor-pointer"
                                        onClick={() => handleSourceFilter(sb.source_type)}
                                    >
                                        {formatSourceType(sb.source_type)} ({sb.count}) ${formatCurrency(sb.total)}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <div className="space-y-1 max-h-[300px] overflow-y-auto">
                            {ledgerRows.map((row) => (
                                <div key={row.line_id} className="rounded border p-2 text-xs">
                                    <div className="flex justify-between">
                                        <span className="font-medium truncate mr-2">
                                            {row.invoice_number
                                                ? `${row.invoice_number}: ${row.description || "Transaction"}`
                                                : row.source_label
                                                  ? `[${row.source_label}] ${row.description || "Transaction"}`
                                                  : row.description || "Transaction"}
                                        </span>
                                        <span className="tabular-nums font-medium whitespace-nowrap">
                                            ${formatCurrency(row.net_amount)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>{row.date}</span>
                                        <span>
                                            {row.invoice_contact || row.contact_name || formatSourceType(row.source_type)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Load more */}
                        {!allLoaded && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? (
                                    <Loader2Icon className="mr-1 size-3 animate-spin" />
                                ) : null}
                                Load more
                            </Button>
                        )}
                        {/* Totals footer */}
                        <div className="flex justify-between border-t pt-2 text-xs font-medium">
                            <span>
                                {activeSourceFilter
                                    ? `Showing: $${formatCurrency(visibleTotal)} (Total: $${formatCurrency(ledgerTotal)})`
                                    : `Total (${ledgerCount} transactions)`}
                            </span>
                            {!activeSourceFilter && (
                                <span className="tabular-nums">${formatCurrency(ledgerTotal)}</span>
                            )}
                        </div>
                        {onOpenTransactions && ledgerRows.length > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full text-xs mt-2"
                                onClick={() => {
                                    const txnFilters: LedgerFilters = {
                                        page: 1,
                                        page_size: 100,
                                        sort: "-date",
                                        ...(monthKey ? {date_from: `${monthKey}-01`, date_to: lastDayOfMonth(monthKey)} : {}),
                                        ...(accountCode ? {account_id: accountCode} : {}),
                                        ...(activeSourceFilter ? {source_type: activeSourceFilter} : {}),
                                    };
                                    onOpenTransactions(txnFilters);
                                }}
                            >
                                <ExternalLinkIcon className="size-3 mr-1.5" />
                                Open in Transactions
                            </Button>
                        )}
                    </div>
                )}

                {/* Top Contacts */}
                {topContacts.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Top Contacts
                        </h4>
                        <div className="space-y-1">
                            {topContacts.map((c) => (
                                <div key={c.contact_name} className="rounded border p-2 text-xs">
                                    <div className="flex justify-between">
                                        <span className="font-medium truncate mr-2">{c.contact_name}</span>
                                        <span className="tabular-nums font-medium whitespace-nowrap">
                                            ${formatCurrency(c.total_net)}
                                        </span>
                                    </div>
                                    <span className="text-muted-foreground">
                                        {c.txn_count} transaction{c.txn_count !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!loading && (accountCode || nodeId) && ledgerRows.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                        No synced journal lines for this account. Sync from Xero to populate.
                    </p>
                )}

                {/* No data states */}
                {!loading && !prov && lineage.length === 0 && breakdown.length === 0 && ledgerRows.length === 0 && !accountCode && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                        No provenance data available for this cell.
                    </p>
                )}
            </div>
        </div>
    );
}
